/**
 * Fires a callback when the viewer "comes back": the tab becomes visible again
 * (phone unlocked, tab switched back), the page is restored from the
 * back/forward cache, or the browser regains network. Browsers throttle or
 * freeze timers and sockets in hidden tabs, so the player can be seconds
 * behind — and a paused room never sends heartbeats to correct it.
 *
 * Bursts (visibilitychange + online + pageshow often arrive together) are
 * collapsed into one callback.
 */

/** Subscribe to return events; returns an unsubscribe function. */
export function onViewerReturn(handler: () => void, debounceMs = 250): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const fire = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      handler();
    }, debounceMs);
  };

  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') fire();
  };
  const onPageShow = (e: Event): void => {
    // WHY: pageshow also fires on first load; only a bfcache restore matters.
    if ((e as PageTransitionEvent).persisted) fire();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', fire);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    if (timer !== null) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', fire);
    window.removeEventListener('pageshow', onPageShow);
  };
}
